import { CSSProperties } from 'react';

/** One cell as the table draws it: its text, how far it reaches, and the file's own styling. */
export type SheetCell = {
  colSpan: number;
  rowSpan: number;
  style: CSSProperties;
  text: string;
};

export type SheetRow = {
  cells: SheetCell[];
  height?: number;
};

/** One sheet of a workbook. `columns` are pixel widths, in order. */
export type SheetGrid = {
  columns: number[];
  name: string;
  rows: SheetRow[];
};

export type CellSpan = { colSpan: number; rowSpan: number };

/** A merged block: where it starts, how far it reaches, and which cells it swallows. */
export type SheetMerges = {
  covered: Set<string>;
  spans: Map<string, CellSpan>;
};

export const cellKey = (row: number, column: number): string => `${row}:${column}`;

/** `A` is 1, `Z` is 26, `AA` is 27 — a base-26 count with no zero digit. */
export const columnIndexOf = (letters: string): number =>
  [...letters.toUpperCase()].reduce((index, letter) => index * 26 + (letter.charCodeAt(0) - 64), 0);

const RANGE = /^([A-Za-z]+)(\d+):([A-Za-z]+)(\d+)$/;

/**
 * Excel's merged ranges, as what a table needs: a span on the cell that starts each block, and
 * the set of cells the block covers, which must not be drawn at all.
 *
 * A merged title across six columns is the first thing on every export this product writes, so a
 * reader that ignores merges shows the title in column A and five empty cells beside it.
 */
export const parseMergeRanges = (ranges: string[]): SheetMerges => {
  const merges: SheetMerges = { covered: new Set(), spans: new Map() };

  for (const range of ranges) {
    const match = RANGE.exec(range.trim());

    if (!match) continue;

    const [, startLetters, startRow, endLetters, endRow] = match;
    const top = Number(startRow);
    const bottom = Number(endRow);
    const left = columnIndexOf(startLetters);
    const right = columnIndexOf(endLetters);

    merges.spans.set(cellKey(top, left), {
      colSpan: right - left + 1,
      rowSpan: bottom - top + 1,
    });

    for (let row = top; row <= bottom; row += 1) {
      for (let column = left; column <= right; column += 1) {
        if (row !== top || column !== left) merges.covered.add(cellKey(row, column));
      }
    }
  }

  return merges;
};

// A spreadsheet's used range is ragged — row 3 may hold six cells and row 4 only two. The table
// needs a rectangle, so every row is padded to the widest one.
export const squareOff = (rows: string[][]): string[][] => {
  const width = rows.reduce((widest, row) => Math.max(widest, row.length), 0);

  return rows.map((row) => [...row, ...Array<string>(width - row.length).fill('')]);
};

// Excel puts one in front of a CSV so other programs read its encoding; it is not part of the
// first heading, and a reader that keeps it shows an invisible character before the first one.
const BYTE_ORDER_MARK = String.fromCharCode(0xfeff);

const stripByteOrderMark = (text: string): string =>
  text.startsWith(BYTE_ORDER_MARK) ? text.slice(1) : text;

/**
 * A CSV as rows, by the rules a spreadsheet writes it with: fields separated by commas, a field
 * holding a comma or a newline wrapped in quotes, and a quote inside such a field doubled.
 *
 * Hand-written rather than brought in: this is the whole of RFC 4180 that a reader needs, and a
 * parser dependency for forty lines would be the heavier choice.
 */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let isQuoted = false;

  // The file may arrive with any of the three line endings, and a byte-order mark in front.
  const source = stripByteOrderMark(text).replace(/\r\n?/g, '\n');

  const endField = (): void => {
    row.push(field);
    field = '';
  };

  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (isQuoted) {
      if (character !== '"') {
        field += character;
        continue;
      }

      // Two quotes inside a quoted field are one literal quote; a single one closes the field.
      if (source[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        isQuoted = false;
      }

      continue;
    }

    if (character === '"' && field === '') isQuoted = true;
    else if (character === ',') endField();
    else if (character === '\n') endRow();
    else field += character;
  }

  // A file ending in a newline has no last row; one ending mid-field does.
  if (field !== '' || row.length) endRow();

  return squareOff(rows);
};

/** A CSV has no styling of its own, so it is drawn as the plain rectangle it is. */
export const csvToGrid = (text: string): SheetGrid => {
  const rows = parseCsv(text);

  return {
    columns: Array<number>(rows[0]?.length ?? 0).fill(140),
    name: '',
    rows: rows.map((cells) => ({
      cells: cells.map((cell) => ({ colSpan: 1, rowSpan: 1, style: {}, text: cell })),
    })),
  };
};
